import React, { useState, useEffect } from 'react';
import {
  AlertCircle,
  Wifi,
  Smartphone,
  Music,
  Globe,
  MessageCircle,
  Info,
  ChevronDown,
  CheckCircle,
  Shield,
  CreditCard,
  Calendar,
  Package,
  TrendingDown,
  MapPin,
  Hash,
  Phone
} from 'lucide-react';
import {
  Typography,
  Paper,
  Box,
  Grid,
  Tooltip,
  Collapse,
  Button,
  Chip
} from '@mui/material';
import { motion } from 'framer-motion';
import './ReviewAndConfirm.css';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30
    }
  }
};

const featureVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30
    }
  }
};

// Helper function for cost formatting
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount);
};

const ReviewAndConfirm = ({
  planDetails,
  userDetails,
  subscriptionDetails,
  onEdit,
  onProceedToPayment,
  
}) => {
  const [verizonExpanded, setVerizonExpanded] = useState(true);
  const [circledinExpanded, setCircledinExpanded] = useState(false);

  // Get line count from userDetails
  const lineCount = userDetails?.numLines || 1;

  // Determine if eSIM is being used
  const isEsim = userDetails?.esim || false;

  // UPDATED: Get activation fee from props or use default
  const perLineActivationFee = planDetails?.activationFeePerLine || 35.00;
  const totalActivationFee = planDetails?.totalActivationFee || (perLineActivationFee * lineCount);

  // UPDATED: Calculate costs with proper multiplication
  const verizonMonthlyCost = Number(planDetails?.monthlyRate || 43.00);
  const verizonTaxes = Number(planDetails?.taxes || 5.00);
  const verizonTotal = verizonMonthlyCost + totalActivationFee;

  // UPDATED: Subscription values from props or defaults
  const isAnnual = subscriptionDetails?.type === 'annual';
  const perLineSubscriptionCost = subscriptionDetails?.perLineSubscriptionCost || (isAnnual ? 96.00 : 36.00);
  const totalSubscriptionCost = subscriptionDetails?.totalSubscriptionCost || (perLineSubscriptionCost * lineCount);
  const monthlySubscriptionCost = isAnnual ? 8.00 : 12.00;

  // UPDATED: Only calculate shipping if NOT using eSIM
  const isExpedited = subscriptionDetails?.shipping === 'expedited';
  const shippingCost = isEsim ? 0 : (isExpedited ? 15.00 : 8.00);
  const circledinTotal = totalSubscriptionCost + shippingCost;

  const totalDue = verizonTotal + circledinTotal;

  // Calculate savings
  const previousBillAmount = Number(userDetails?.previousBill || 42.00);
  const monthlySavings = previousBillAmount - (verizonMonthlyCost / lineCount); // Per line savings
  const annualSavings = monthlySavings * 12;

  // Function to mask PIN for security
  const maskPin = (pin) => {
    if (!pin) return "••••••";
    return "•".repeat(pin.length);
  };

  // Features data with monochrome icons (keeping subtle color in containers)
  const features = [
    {
      icon: Wifi,
      title: "Data",
      description: "5G Nationwide / 4G LTE Data",
    },
    {
      icon: Smartphone,
      title: "Hotspot",
      description: "5GB Mobile Hotspot",
    },
    {
      icon: Music,
      title: "Streaming",
      description: "480p SD Streaming",
    },
    {
      icon: Globe,
      title: "International",
      description: "Text internationally to 200+ countries",
    },
    {
      icon: MessageCircle,
      title: "Talk & Text",
      description: "Unlimited talk and text in the US",
    }
  ];

  return (
    <motion.div
      className="rc-container"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <Box className="rc-header">
        <Typography variant="h4" className="rc-title">
          Review Your Order
        </Typography>
        <Typography variant="body1" className="rc-subtitle">
          Please verify all details before proceeding to payment
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid item xs={12} md={7}>
          <motion.div variants={cardVariants}>
            <Paper className="rc-card rc-card-level-3 rc-plan-details">
              <Box className="rc-card-header">
                <Typography variant="overline" className="rc-card-overline">
                  PLAN DETAILS
                </Typography>
                <Chip
                  label="Savings"
                  color="success"
                  icon={<TrendingDown size={14} />}
                  className="rc-savings-chip"
                />
              </Box>

              <Box className="rc-carrier-identifier">
                <Typography variant="caption" className="rc-carrier-label">
                  {planDetails?.circleName || 'CURRY3'}
                </Typography>
                <Typography variant="h5" className="rc-plan-name">
                  {planDetails?.planName || "Verizon Unlimited Plusssss"}
                </Typography>
              </Box>

              {monthlySavings > 0 && (
                <Box className="rc-savings-banner">
                  <TrendingDown size={18} />
                  <Typography variant="body2">
                    You're saving <strong>{formatCurrency(monthlySavings)}/month</strong> compared to your previous plan
                    (<strong>{formatCurrency(annualSavings)}/year</strong>)
                  </Typography>
                </Box>
              )}

              <Box className="rc-features-grid">
                {features.map((feature, index) => (
                  <motion.div
                    className="rc-feature-item"
                    key={index}
                    variants={featureVariants}
                    whileHover={{ scale: 1.03, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}
                  >
                    <Box className="rc-feature-icon-container">
                      <feature.icon size={18} color="#555555" />
                    </Box>
                    <Box className="rc-feature-content">
                      <Typography variant="subtitle2" className="rc-feature-title">
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" className="rc-feature-description">
                        {feature.description}
                      </Typography>
                    </Box>
                  </motion.div>
                ))}
              </Box>
            </Paper>
          </motion.div>

          {/* Device & Location Information section */}
          <motion.div variants={cardVariants}>
            <Paper className="rc-card rc-card-level-2">
              <Typography variant="overline" className="rc-card-overline">
                DEVICE & LOCATION INFORMATION
              </Typography>

              {/* Display all line details when multiple lines exist */}
              {userDetails?.lineDetails && userDetails.lineDetails.length > 0 ? (
                <Box>
                  {userDetails.lineDetails.map((line, index) => (
                    <Box key={`line-${index}`} sx={{
                      mb: index < userDetails.lineDetails.length - 1 ? 3 : 0,
                      pb: index < userDetails.lineDetails.length - 1 ? 2 : 0,
                      borderBottom: index < userDetails.lineDetails.length - 1 ? '1px solid #eaeaea' : 'none'
                    }}>
                      {userDetails.lineDetails.length > 1 && (
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 600,
                            color: '#666',
                            mb: 1
                          }}
                        >
                          Line {index + 1}
                        </Typography>
                      )}

                      <Box className="rc-info-grid">
                        {/* IMEI information */}
                        <Box className="rc-info-item">
                          <Box className="rc-info-icon-label">
                            <Smartphone size={16} />
                            <Typography variant="caption">IMEI</Typography>
                          </Box>
                          <Typography variant="body2" className="rc-info-value">
                            {line.imei || "Not provided"}
                          </Typography>
                        </Box>

                        {/* eSIM information - only show if this line has eSIM */}
                        {line.esim && (
                          <Box className="rc-info-item">
                            <Box className="rc-info-icon-label">
                              <Smartphone size={16} />
                              <Typography variant="caption">eSIM</Typography>
                            </Box>
                            <Typography variant="body2" className="rc-info-value">
                              {line.imei2 || "Not provided"}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                // Fallback if no line details exist
                <Box className="rc-info-grid">
                  <Box className="rc-info-item">
                    <Box className="rc-info-icon-label">
                      <Smartphone size={16} />
                      <Typography variant="caption">IMEI</Typography>
                    </Box>
                    <Typography variant="body2" className="rc-info-value">
                      {userDetails?.imei || "Not provided"}
                    </Typography>
                  </Box>

                  {/* Only show IMEI2/eSIM if eSIM is enabled */}
                  {isEsim && (
                    <Box className="rc-info-item">
                      <Box className="rc-info-icon-label">
                        <Smartphone size={16} />
                        <Typography variant="caption">eSIM</Typography>
                      </Box>
                      <Typography variant="body2" className="rc-info-value">
                        {userDetails?.imei2 || "Not provided"}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Location information */}
              <Box className="rc-info-grid" sx={{ mt: 3 }}>
                <Box className="rc-info-item">
                  <Box className="rc-info-icon-label">
                    <MapPin size={16} />
                    <Typography variant="caption">ZIP CODE</Typography>
                  </Box>
                  <Typography variant="body2" className="rc-info-value">
                    {userDetails?.zip_code || "Not provided"}
                  </Typography>
                </Box>

                <Box className="rc-info-item">
                  <Box className="rc-info-icon-label">
                    <Hash size={16} />
                    <Typography variant="caption">AREA CODE</Typography>
                  </Box>
                  <Typography variant="body2" className="rc-info-value">
                    {userDetails?.area_code || "Not provided"}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </motion.div>

          <motion.div variants={cardVariants}>
            <Paper className="rc-card rc-card-level-1">
              <Typography variant="overline" className="rc-card-overline">
                ACCOUNT INFORMATION
              </Typography>

              <Box className="rc-info-grid">
                <Box className="rc-info-item">
                  <Box className="rc-info-icon-label">
                    <Phone size={16} />
                    <Typography variant="caption">PORTING NUMBER</Typography>
                  </Box>
                  <Typography variant="body2" className="rc-info-value">
                    {userDetails?.port_number
                      ? (userDetails?.phoneNumbers?.[0] || "Not provided")
                      : "New number will be assigned"}
                  </Typography>
                </Box>

                {/* Show account number and PIN only when porting is enabled */}
                {userDetails?.port_number && (
                  <>
                    <Box className="rc-info-item">
                      <Box className="rc-info-icon-label">
                        <CreditCard size={16} />
                        <Typography variant="caption">ACCOUNT NUMBER</Typography>
                      </Box>
                      <Typography variant="body2" className="rc-info-value">
                        {userDetails?.accountNumber || "Not provided"}
                      </Typography>
                    </Box>

                    <Box className="rc-info-item">
                      <Box className="rc-info-icon-label">
                        <Hash size={16} />
                        <Typography variant="caption">PIN</Typography>
                      </Box>
                      <Typography variant="body2" className="rc-info-value">
                        {maskPin(userDetails?.pin)}
                      </Typography>
                    </Box>
                  </>
                )}

                <Box className="rc-info-item">
                  <Box className="rc-info-icon-label">
                    <Smartphone size={16} />
                    <Typography variant="caption">PREVIOUS CARRIER</Typography>
                  </Box>
                  <Typography variant="body2" className="rc-info-value">
                    {userDetails?.previousCarrier || "N/A"}
                  </Typography>
                </Box>

                <Box className="rc-info-item">
                  <Box className="rc-info-icon-label">
                    <Calendar size={16} />
                    <Typography variant="caption">SUBSCRIPTION</Typography>
                  </Box>
                  <Typography variant="body2" className="rc-info-value">
                    {isAnnual ? "Annual Plan" : "3-Month Plan"}
                  </Typography>
                </Box>

                {/* Only show shipping method if physical SIM is needed */}
                {!isEsim && (
                  <Box className="rc-info-item">
                    <Box className="rc-info-icon-label">
                      <Package size={16} />
                      <Typography variant="caption">SHIPPING</Typography>
                    </Box>
                    <Typography variant="body2" className="rc-info-value">
                      {isExpedited ? "Expedited (2-3 days)" : "Standard (5-7 days)"}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </motion.div>
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} md={5}>
          <motion.div variants={cardVariants}>
            <Paper className="rc-card rc-card-level-2">
              <Typography variant="overline" className="rc-card-overline">
                ORDER SUMMARY
              </Typography>

              {/* Verizon Charges */}
              <Box className="rc-charge-section">
                <Box
                  className={`rc-charge-header ${verizonExpanded ? "expanded" : ""}`}
                  onClick={() => setVerizonExpanded(!verizonExpanded)}
                >
                  <Box className="rc-charge-title-container">
                    <Typography variant="subtitle1" className="rc-charge-title">
                      Verizon Charges
                    </Typography>
                    <Typography variant="caption" className="rc-charge-subtitle">
                      Carrier services & fees
                    </Typography>
                  </Box>
                  <Box className="rc-charge-amount-container">
                    <Typography variant="subtitle1" className="rc-charge-amount">
                      {formatCurrency(verizonTotal)}
                    </Typography>
                    <ChevronDown
                      size={18}
                      className={`rc-charge-chevron ${verizonExpanded ? "expanded" : ""}`}
                    />
                  </Box>
                </Box>

                <Collapse in={verizonExpanded}>
                  <Box className="rc-charge-details">
                    <Box className="rc-charge-item">
                      <Box className="rc-charge-item-label">
                        <Typography variant="body2">
                          Verizon Mobile Cost
                        </Typography>
                        <Typography variant="caption" className="rc-charge-period">
                          Monthly recurring
                        </Typography>
                      </Box>
                      <Typography variant="body2" className="rc-charge-item-amount">
                        {formatCurrency(verizonMonthlyCost)}
                        {lineCount > 1 && (
                          <Typography variant="caption" sx={{ ml: 0.5 }}>
                            (${(verizonMonthlyCost / lineCount).toFixed(2)}/line × {lineCount})
                          </Typography>
                        )}
                      </Typography>
                    </Box>

                    <Box className="rc-charge-item">
                      <Box className="rc-charge-item-label">
                        <Typography variant="body2">
                          Activation Fee
                        </Typography>
                        <Typography variant="caption" className="rc-charge-period">
                          One-time fee
                        </Typography>
                      </Box>
                      <Typography variant="body2" className="rc-charge-item-amount">
                        {formatCurrency(totalActivationFee)}
                        {lineCount > 1 && (
                          <Typography variant="caption" sx={{ ml: 0.5 }}>
                            (${perLineActivationFee.toFixed(2)}/line × {lineCount})
                          </Typography>
                        )}
                      </Typography>
                    </Box>


                  </Box>
                </Collapse>
              </Box>

              {/* Circledin Charges */}
              <Box className="rc-charge-section">
                <Box
                  className={`rc-charge-header ${circledinExpanded ? "expanded" : ""}`}
                  onClick={() => setCircledinExpanded(!circledinExpanded)}
                >
                  <Box className="rc-charge-title-container">
                    <Typography variant="subtitle1" className="rc-charge-title">
                      Circledin Charges
                    </Typography>
                    <Typography variant="caption" className="rc-charge-subtitle">
                      Platform {!isEsim && '& shipping'} fees
                    </Typography>
                  </Box>
                  <Box className="rc-charge-amount-container">
                    <Typography variant="subtitle1" className="rc-charge-amount">
                      {formatCurrency(circledinTotal)}
                    </Typography>
                    <ChevronDown
                      size={18}
                      className={`rc-charge-chevron ${circledinExpanded ? "expanded" : ""}`}
                    />
                  </Box>
                </Box>

                <Collapse in={circledinExpanded}>
                  <Box className="rc-charge-details">
                    <Box className="rc-charge-item">
                      <Box className="rc-charge-item-label">
                        <Typography variant="body2">
                          Circledin Subscription
                        </Typography>
                        <Typography variant="caption" className="rc-charge-period">
                          {isAnnual ? "Annual billing" : "Quarterly billing"}
                          <Tooltip
                            title={`Equivalent to ${formatCurrency(monthlySubscriptionCost)} per month per line`}
                            arrow
                            placement="top"
                            classes={{ tooltip: "rc-tooltip" }}
                          >
                            <Info size={14} className="rc-info-icon" />
                          </Tooltip>
                        </Typography>
                      </Box>
                      <Typography variant="body2" className="rc-charge-item-amount">
                        {formatCurrency(totalSubscriptionCost)}
                        {lineCount > 1 && (
                          <Typography variant="caption" sx={{ ml: 0.5 }}>
                            (${perLineSubscriptionCost.toFixed(2)}/line × {lineCount})
                          </Typography>
                        )}
                      </Typography>
                    </Box>

                    {/* Only show shipping cost if not using eSIM */}
                    {!isEsim && (
                      <Box className="rc-charge-item">
                        <Box className="rc-charge-item-label">
                          <Typography variant="body2">
                            {isExpedited ? "Expedited Shipping" : "Standard Shipping"}
                          </Typography>
                          <Typography variant="caption" className="rc-charge-period">
                            {isExpedited ? "2-3 business days" : "5-7 business days"}
                          </Typography>
                        </Box>
                        <Typography variant="body2" className="rc-charge-item-amount">
                          {formatCurrency(shippingCost)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </Box>

              {/* Total Due */}
              <Box className="rc-total-section">
                <Box className="rc-total-row">
                  <Box className="rc-total-label-container">
                    <Typography variant="subtitle1" className="rc-total-label">
                      Total Due Today
                    </Typography>
                    <Tooltip
                      title={`Includes all one-time charges, first month of service, and subscription${!isEsim ? ' plus shipping' : ''}`}
                      arrow
                      placement="top"
                      classes={{ tooltip: "rc-tooltip" }}
                    >
                      <Info size={14} className="rc-info-icon" />
                    </Tooltip>
                  </Box>
                  <Typography variant="h5" className="rc-total-amount">
                    {formatCurrency(totalDue)}
                  </Typography>
                </Box>
              </Box>

              {/* Trust Elements */}
              <Box className="rc-trust-elements">
                <Box className="rc-trust-item">
                  <Shield size={16} />
                  <Typography variant="body2">
                    Secure encrypted payment
                  </Typography>
                </Box>
                <Box className="rc-trust-item">
                  <CheckCircle size={16} />
                  <Typography variant="body2">
                    30-day satisfaction guarantee
                  </Typography>
                </Box>
              </Box>

              {/* Terms agreement */}
              <Box className="rc-terms-notice">
                <AlertCircle size={16} />
                <Typography variant="body2">
                  By proceeding, you agree to the terms and conditions of both Circledin and the carrier.
                </Typography>
              </Box>

              {/* Action buttons */}
              <Box className="rc-action-buttons">
                <Button
                  variant="outlined"
                  className="rc-back-button"
                  onClick={onEdit}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  className="rc-proceed-button"
                  onClick={onProceedToPayment}
                  sx={{
                    bgcolor: '#FF4775 !important', // Use the pink/red color from your design system
                    color: 'white',
                    '&:hover': {
                      bgcolor: '#FF385C' // Slightly darker on hover
                    },
                    fontWeight: 600,
                    textTransform: 'none',
                    padding: '12px 24px',
                    borderRadius: '10px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      bgcolor: '#FF385C',
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                      transform: 'translateY(-1px)'
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)'
                    }
                  }}
                >
                  Proceed to Payment
                </Button>
              </Box>
            </Paper>
          </motion.div>
        </Grid>
      </Grid>
    </motion.div>
  );
};

export default ReviewAndConfirm;



/* Working April 29 8pm



import React, { useState } from 'react';
import {
  AlertCircle,
  Wifi,
  Smartphone,
  Music,
  Globe,
  MessageCircle,
  Info,
  ChevronDown,
  CheckCircle,
  Shield,
  CreditCard,
  Calendar,
  Package,
  TrendingDown,
  MapPin,
  Hash,
  Phone
} from 'lucide-react';
import {
  Typography,
  Paper,
  Box,
  Grid,
  Tooltip,
  Collapse,
  Button,
  Chip
} from '@mui/material';
import { motion } from 'framer-motion';
import './ReviewAndConfirm.css';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30
    }
  }
};

const featureVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30
    }
  }
};

// Helper function for cost formatting
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount);
};

const ReviewAndConfirm = ({
  planDetails,
  userDetails,
  subscriptionDetails,
  onEdit,
  onProceedToPayment
}) => {
  const [verizonExpanded, setVerizonExpanded] = useState(true);
  const [circledinExpanded, setCircledinExpanded] = useState(false);

  // Get line count from userDetails
  const lineCount = userDetails?.numLines || 1;

  // UPDATED: Get activation fee from props or use default
  const perLineActivationFee = planDetails?.activationFeePerLine || 35.00;
  const totalActivationFee = planDetails?.totalActivationFee || (perLineActivationFee * lineCount);

  // UPDATED: Calculate costs with proper multiplication
  const verizonMonthlyCost = Number(planDetails?.monthlyRate || 43.00);
  const verizonTaxes = Number(planDetails?.taxes || 5.00);
  const verizonTotal = verizonMonthlyCost + totalActivationFee + verizonTaxes;

  // UPDATED: Subscription values from props or defaults
  const isAnnual = subscriptionDetails?.type === 'annual';
  const perLineSubscriptionCost = subscriptionDetails?.perLineSubscriptionCost || (isAnnual ? 96.00 : 36.00);
  const totalSubscriptionCost = subscriptionDetails?.totalSubscriptionCost || (perLineSubscriptionCost * lineCount);
  const monthlySubscriptionCost = isAnnual ? 8.00 : 12.00;

  const isExpedited = subscriptionDetails?.shipping === 'expedited';
  const shippingCost = isExpedited ? 15.00 : 8.00;
  const circledinTotal = totalSubscriptionCost + shippingCost;

  const totalDue = verizonTotal + circledinTotal;

  // Calculate savings
  const previousBillAmount = Number(userDetails?.previousBill || 42.00);
  const monthlySavings = previousBillAmount - (verizonMonthlyCost / lineCount); // Per line savings
  const annualSavings = monthlySavings * 12;

  // Function to mask PIN for security
  const maskPin = (pin) => {
    if (!pin) return "••••••";
    return "•".repeat(pin.length);
  };

  // Features data with monochrome icons (keeping subtle color in containers)
  const features = [
    {
      icon: Wifi,
      title: "Data",
      description: "5G Nationwide / 4G LTE Data",
    },
    {
      icon: Smartphone,
      title: "Hotspot",
      description: "5GB Mobile Hotspot",
    },
    {
      icon: Music,
      title: "Streaming",
      description: "480p SD Streaming",
    },
    {
      icon: Globe,
      title: "International",
      description: "Text internationally to 200+ countries",
    },
    {
      icon: MessageCircle,
      title: "Talk & Text",
      description: "Unlimited talk and text in the US",
    }
  ];

  return (
    <motion.div
      className="rc-container"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <Box className="rc-header">
        <Typography variant="h4" className="rc-title">
          Review Your Order
        </Typography>
        <Typography variant="body1" className="rc-subtitle">
          Please verify all details before proceeding to payment
        </Typography>
      </Box>

      <Grid container spacing={3}>
        
        <Grid item xs={12} md={7}>
          <motion.div variants={cardVariants}>
            <Paper className="rc-card rc-card-level-3 rc-plan-details">
              <Box className="rc-card-header">
                <Typography variant="overline" className="rc-card-overline">
                  PLAN DETAILS
                </Typography>
                <Chip
                  label="Savings"
                  color="success"
                  icon={<TrendingDown size={14} />}
                  className="rc-savings-chip"
                />
              </Box>

              <Box className="rc-carrier-identifier">
                <Typography variant="caption" className="rc-carrier-label">
                  {planDetails?.circleName || 'CURRY3'}
                </Typography>
                <Typography variant="h5" className="rc-plan-name">
                  {planDetails?.planName || "Verizon Unlimited Plusssss"}
                </Typography>
              </Box>

              {monthlySavings > 0 && (
                <Box className="rc-savings-banner">
                  <TrendingDown size={18} />
                  <Typography variant="body2">
                    You're saving <strong>{formatCurrency(monthlySavings)}/month</strong> compared to your previous plan
                    (<strong>{formatCurrency(annualSavings)}/year</strong>)
                  </Typography>
                </Box>
              )}

              <Box className="rc-features-grid">
                {features.map((feature, index) => (
                  <motion.div
                    className="rc-feature-item"
                    key={index}
                    variants={featureVariants}
                    whileHover={{ scale: 1.03, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}
                  >
                    <Box className="rc-feature-icon-container">
                      <feature.icon size={18} color="#555555" />
                    </Box>
                    <Box className="rc-feature-content">
                      <Typography variant="subtitle2" className="rc-feature-title">
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" className="rc-feature-description">
                        {feature.description}
                      </Typography>
                    </Box>
                  </motion.div>
                ))}
              </Box>
            </Paper>
          </motion.div>

          
          <motion.div variants={cardVariants}>
            <Paper className="rc-card rc-card-level-2">
              <Typography variant="overline" className="rc-card-overline">
                DEVICE & LOCATION INFORMATION
              </Typography>

              
              {userDetails?.lineDetails && userDetails.lineDetails.length > 0 ? (
                <Box>
                  {userDetails.lineDetails.map((line, index) => (
                    <Box key={`line-${index}`} sx={{
                      mb: index < userDetails.lineDetails.length - 1 ? 3 : 0,
                      pb: index < userDetails.lineDetails.length - 1 ? 2 : 0,
                      borderBottom: index < userDetails.lineDetails.length - 1 ? '1px solid #eaeaea' : 'none'
                    }}>
                      {userDetails.lineDetails.length > 1 && (
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 600,
                            color: '#666',
                            mb: 1
                          }}
                        >
                          Line {index + 1}
                        </Typography>
                      )}

                      <Box className="rc-info-grid">
                        
                        <Box className="rc-info-item">
                          <Box className="rc-info-icon-label">
                            <Smartphone size={16} />
                            <Typography variant="caption">IMEI</Typography>
                          </Box>
                          <Typography variant="body2" className="rc-info-value">
                            {line.imei || "Not provided"}
                          </Typography>
                        </Box>

                        
                        {line.esim && (
                          <Box className="rc-info-item">
                            <Box className="rc-info-icon-label">
                              <Smartphone size={16} />
                              <Typography variant="caption">eSIM</Typography>
                            </Box>
                            <Typography variant="body2" className="rc-info-value">
                              {line.imei2 || "Not provided"}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                // Fallback if no line details exist
                <Box className="rc-info-grid">
                  <Box className="rc-info-item">
                    <Box className="rc-info-icon-label">
                      <Smartphone size={16} />
                      <Typography variant="caption">IMEI</Typography>
                    </Box>
                    <Typography variant="body2" className="rc-info-value">
                      Not provided
                    </Typography>
                  </Box>
                </Box>
              )}

              
              <Box className="rc-info-grid" sx={{ mt: 3 }}>
                <Box className="rc-info-item">
                  <Box className="rc-info-icon-label">
                    <MapPin size={16} />
                    <Typography variant="caption">ZIP CODE</Typography>
                  </Box>
                  <Typography variant="body2" className="rc-info-value">
                    {userDetails?.zip_code || "Not provided"}
                  </Typography>
                </Box>

                <Box className="rc-info-item">
                  <Box className="rc-info-icon-label">
                    <Hash size={16} />
                    <Typography variant="caption">AREA CODE</Typography>
                  </Box>
                  <Typography variant="body2" className="rc-info-value">
                    {userDetails?.area_code || "Not provided"}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </motion.div>

          <motion.div variants={cardVariants}>
            <Paper className="rc-card rc-card-level-1">
              <Typography variant="overline" className="rc-card-overline">
                ACCOUNT INFORMATION
              </Typography>

              <Box className="rc-info-grid">
                <Box className="rc-info-item">
                  <Box className="rc-info-icon-label">
                    <Phone size={16} />
                    <Typography variant="caption">PORTING NUMBER</Typography>
                  </Box>
                  <Typography variant="body2" className="rc-info-value">
                    {userDetails?.port_number
                      ? (userDetails?.phoneNumbers?.[0] || "Not provided")
                      : "New number will be assigned"}
                  </Typography>
                </Box>

                
                {userDetails?.port_number && (
                  <>
                    <Box className="rc-info-item">
                      <Box className="rc-info-icon-label">
                        <CreditCard size={16} />
                        <Typography variant="caption">ACCOUNT NUMBER</Typography>
                      </Box>
                      <Typography variant="body2" className="rc-info-value">
                        {userDetails?.accountNumber || "Not provided"}
                      </Typography>
                    </Box>

                    <Box className="rc-info-item">
                      <Box className="rc-info-icon-label">
                        <Hash size={16} />
                        <Typography variant="caption">PIN</Typography>
                      </Box>
                      <Typography variant="body2" className="rc-info-value">
                        {maskPin(userDetails?.pin)}
                      </Typography>
                    </Box>
                  </>
                )}

                <Box className="rc-info-item">
                  <Box className="rc-info-icon-label">
                    <Smartphone size={16} />
                    <Typography variant="caption">PREVIOUS CARRIER</Typography>
                  </Box>
                  <Typography variant="body2" className="rc-info-value">
                    {userDetails?.previousCarrier || "MINTY"}
                  </Typography>
                </Box>

                <Box className="rc-info-item">
                  <Box className="rc-info-icon-label">
                    <Calendar size={16} />
                    <Typography variant="caption">SUBSCRIPTION</Typography>
                  </Box>
                  <Typography variant="body2" className="rc-info-value">
                    {isAnnual ? "Annual Plan" : "3-Month Plan"}
                  </Typography>
                </Box>

                <Box className="rc-info-item">
                  <Box className="rc-info-icon-label">
                    <Package size={16} />
                    <Typography variant="caption">SHIPPING</Typography>
                  </Box>
                  <Typography variant="body2" className="rc-info-value">
                    {isExpedited ? "Expedited (2-3 days)" : "Standard (5-7 days)"}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </motion.div>
        </Grid>

        
        <Grid item xs={12} md={5}>
          <motion.div variants={cardVariants}>
            <Paper className="rc-card rc-card-level-2">
              <Typography variant="overline" className="rc-card-overline">
                ORDER SUMMARY
              </Typography>

              
              <Box className="rc-charge-section">
                <Box
                  className={`rc-charge-header ${verizonExpanded ? "expanded" : ""}`}
                  onClick={() => setVerizonExpanded(!verizonExpanded)}
                >
                  <Box className="rc-charge-title-container">
                    <Typography variant="subtitle1" className="rc-charge-title">
                      Verizon Charges
                    </Typography>
                    <Typography variant="caption" className="rc-charge-subtitle">
                      Carrier services & fees
                    </Typography>
                  </Box>
                  <Box className="rc-charge-amount-container">
                    <Typography variant="subtitle1" className="rc-charge-amount">
                      {formatCurrency(verizonTotal)}
                    </Typography>
                    <ChevronDown
                      size={18}
                      className={`rc-charge-chevron ${verizonExpanded ? "expanded" : ""}`}
                    />
                  </Box>
                </Box>

                <Collapse in={verizonExpanded}>
                  <Box className="rc-charge-details">
                    <Box className="rc-charge-item">
                      <Box className="rc-charge-item-label">
                        <Typography variant="body2">
                          Verizon Mobile Cost
                        </Typography>
                        <Typography variant="caption" className="rc-charge-period">
                          Monthly recurring
                        </Typography>
                      </Box>
                      <Typography variant="body2" className="rc-charge-item-amount">
                        {formatCurrency(verizonMonthlyCost)}
                        {lineCount > 1 && (
                          <Typography variant="caption" sx={{ ml: 0.5 }}>
                            (${(verizonMonthlyCost / lineCount).toFixed(2)}/line × {lineCount})
                          </Typography>
                        )}
                      </Typography>
                    </Box>

                    <Box className="rc-charge-item">
                      <Box className="rc-charge-item-label">
                        <Typography variant="body2">
                          Activation Fee
                        </Typography>
                        <Typography variant="caption" className="rc-charge-period">
                          One-time fee
                        </Typography>
                      </Box>
                      <Typography variant="body2" className="rc-charge-item-amount">
                        {formatCurrency(totalActivationFee)}
                        {lineCount > 1 && (
                          <Typography variant="caption" sx={{ ml: 0.5 }}>
                            (${perLineActivationFee.toFixed(2)}/line × {lineCount})
                          </Typography>
                        )}
                      </Typography>
                    </Box>

                    <Box className="rc-charge-item">
                      <Box className="rc-charge-item-label">
                        <Typography variant="body2">
                          Estimated Taxes & Fees
                        </Typography>
                      </Box>
                      <Typography variant="body2" className="rc-charge-item-amount">
                        {formatCurrency(verizonTaxes)}
                        {lineCount > 1 && (
                          <Typography variant="caption" sx={{ ml: 0.5 }}>
                            (${(verizonTaxes / lineCount).toFixed(2)}/line × {lineCount})
                          </Typography>
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </Collapse>
              </Box>

              
              <Box className="rc-charge-section">
                <Box
                  className={`rc-charge-header ${circledinExpanded ? "expanded" : ""}`}
                  onClick={() => setCircledinExpanded(!circledinExpanded)}
                >
                  <Box className="rc-charge-title-container">
                    <Typography variant="subtitle1" className="rc-charge-title">
                      Circledin Charges
                    </Typography>
                    <Typography variant="caption" className="rc-charge-subtitle">
                      Platform & shipping fees
                    </Typography>
                  </Box>
                  <Box className="rc-charge-amount-container">
                    <Typography variant="subtitle1" className="rc-charge-amount">
                      {formatCurrency(circledinTotal)}
                    </Typography>
                    <ChevronDown
                      size={18}
                      className={`rc-charge-chevron ${circledinExpanded ? "expanded" : ""}`}
                    />
                  </Box>
                </Box>

                <Collapse in={circledinExpanded}>
                  <Box className="rc-charge-details">
                    <Box className="rc-charge-item">
                      <Box className="rc-charge-item-label">
                        <Typography variant="body2">
                          Circledin Subscription
                        </Typography>
                        <Typography variant="caption" className="rc-charge-period">
                          {isAnnual ? "Annual billing" : "Quarterly billing"}
                          <Tooltip
                            title={`Equivalent to ${formatCurrency(monthlySubscriptionCost)} per month per line`}
                            arrow
                            placement="top"
                            classes={{ tooltip: "rc-tooltip" }}
                          >
                            <Info size={14} className="rc-info-icon" />
                          </Tooltip>
                        </Typography>
                      </Box>
                      <Typography variant="body2" className="rc-charge-item-amount">
                        {formatCurrency(totalSubscriptionCost)}
                        {lineCount > 1 && (
                          <Typography variant="caption" sx={{ ml: 0.5 }}>
                            (${perLineSubscriptionCost.toFixed(2)}/line × {lineCount})
                          </Typography>
                        )}
                      </Typography>
                    </Box>

                    <Box className="rc-charge-item">
                      <Box className="rc-charge-item-label">
                        <Typography variant="body2">
                          {isExpedited ? "Expedited Shipping" : "Standard Shipping"}
                        </Typography>
                        <Typography variant="caption" className="rc-charge-period">
                          {isExpedited ? "2-3 business days" : "5-7 business days"}
                        </Typography>
                      </Box>
                      <Typography variant="body2" className="rc-charge-item-amount">
                        {formatCurrency(shippingCost)}
                      </Typography>
                    </Box>
                  </Box>
                </Collapse>
              </Box>

              
              <Box className="rc-total-section">
                <Box className="rc-total-row">
                  <Box className="rc-total-label-container">
                    <Typography variant="subtitle1" className="rc-total-label">
                      Total Due Today
                    </Typography>
                    <Tooltip
                      title="Includes all one-time charges, first month of service, and subscription"
                      arrow
                      placement="top"
                      classes={{ tooltip: "rc-tooltip" }}
                    >
                      <Info size={14} className="rc-info-icon" />
                    </Tooltip>
                  </Box>
                  <Typography variant="h5" className="rc-total-amount">
                    {formatCurrency(totalDue)}
                  </Typography>
                </Box>
              </Box>

              
              <Box className="rc-trust-elements">
                <Box className="rc-trust-item">
                  <Shield size={16} />
                  <Typography variant="body2">
                    Secure encrypted payment
                  </Typography>
                </Box>
                <Box className="rc-trust-item">
                  <CheckCircle size={16} />
                  <Typography variant="body2">
                    30-day satisfaction guarantee
                  </Typography>
                </Box>
              </Box>

              
              <Box className="rc-terms-notice">
                <AlertCircle size={16} />
                <Typography variant="body2">
                  By proceeding, you agree to the terms and conditions of both Circledin and the carrier.
                </Typography>
              </Box>

              
              <Box className="rc-action-buttons">
                <Button
                  variant="outlined"
                  className="rc-back-button"
                  onClick={onEdit}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  className="rc-proceed-button"
                  onClick={onProceedToPayment}
                >
                  Proceed to Payment
                </Button>
              </Box>
            </Paper>
          </motion.div>
        </Grid>
      </Grid>
    </motion.div>
  );
};

export default ReviewAndConfirm;



*/